import type { AdCreative, AdditionalAd, CreativeAsset } from '../types/metaAdDraft.js';

// C7-extension migration: the creative copy moved from single strings
// (headline / primaryText / description) to arrays (headlines / primaryTexts /
// descriptions). D3.4 extension: the asset fields moved from flat
// imageUrl/imagePath/videoUrl/videoPath/videoThumbnailUrl/videoThumbnailPath (+
// the separate ad.storyCreativeImage) to two `CreativeAsset` slots
// (feedAsset / storyAsset), each image XOR video with an optional thumbnail.
//
// This pure helper normalises EITHER shape to the current shape so legacy drafts
// read + autosave cleanly. Applied at three boundaries: the server read path
// (getMetaAdDraft / listMetaAdDrafts via normalizeStoredDraft), the zod request
// preprocess (ad-level, so it can fold ad.storyCreativeImage into the creative),
// and the client draftToInput. Idempotent — re-running on an already-current
// creative is a no-op.
//
// `legacyStoryImage` is the OLD ad.storyCreativeImage ({imageUrl, imagePath}),
// passed in because it lived on `ad`, not `creative`. When the creative already
// carries a new `storyAsset` it wins; otherwise we migrate from this.

const EMPTY_ASSET: CreativeAsset = { type: null, url: null, path: null, thumbnailUrl: null, thumbnailPath: null };

// Headlines/primaryTexts default to a single empty input (min 1 visible);
// descriptions default to a single empty input too (the AM can remove it to 0).
function toArray(arrValue: unknown, legacyValue: unknown): string[] {
  if (Array.isArray(arrValue)) {
    return arrValue.filter((x): x is string => typeof x === 'string');
  }
  if (typeof legacyValue === 'string') return [legacyValue];
  return [''];
}

function str(v: unknown): string | null {
  return typeof v === 'string' && v !== '' ? v : null;
}

// Coerce a possibly-new-shape asset object to a CreativeAsset, or null if absent.
function asAsset(v: unknown): CreativeAsset | null {
  if (!v || typeof v !== 'object') return null;
  const a = v as Record<string, unknown>;
  // Only treat as a populated new-shape asset if it carries the new keys.
  if (!('type' in a) && !('url' in a)) return null;
  const type = a.type === 'image' || a.type === 'video' ? a.type : null;
  return {
    type,
    url: str(a.url),
    path: str(a.path),
    thumbnailUrl: str(a.thumbnailUrl),
    thumbnailPath: str(a.thumbnailPath),
    // Clone (Part B): reused media ids survive the shape migration (absent on every uploaded asset).
    ...(str(a.imageHash) ? { imageHash: str(a.imageHash) } : {}),
    ...(str(a.videoId) ? { videoId: str(a.videoId) } : {}),
  };
}

// Build the Feed slot from either the new feedAsset or the legacy flat fields.
function migrateFeedAsset(c: Record<string, unknown>): CreativeAsset {
  const fresh = asAsset(c.feedAsset);
  if (fresh) return fresh;
  const assetType = c.assetType === 'video' ? 'video' : 'image';
  if (assetType === 'video') {
    const url = str(c.videoUrl);
    return {
      type: url ? 'video' : null,
      url,
      path: str(c.videoPath),
      thumbnailUrl: str(c.videoThumbnailUrl),
      thumbnailPath: str(c.videoThumbnailPath),
    };
  }
  const url = str(c.imageUrl);
  return { type: url ? 'image' : null, url, path: str(c.imagePath), thumbnailUrl: null, thumbnailPath: null };
}

// Build the Story slot from either the new storyAsset or the legacy
// ad.storyCreativeImage ({imageUrl, imagePath} — always an image).
function migrateStoryAsset(c: Record<string, unknown>, legacyStoryImage: unknown): CreativeAsset {
  const fresh = asAsset(c.storyAsset);
  if (fresh) return fresh;
  if (legacyStoryImage && typeof legacyStoryImage === 'object') {
    const s = legacyStoryImage as Record<string, unknown>;
    const url = str(s.imageUrl);
    if (url) return { type: 'image', url, path: str(s.imagePath), thumbnailUrl: null, thumbnailPath: null };
  }
  return { ...EMPTY_ASSET };
}

// Phase 2a — per-entry assetType on additional ads. Entries created before the
// field existed default to 'image' (the only mode Phase 2 supported). Pure +
// idempotent; applied at the same boundaries as migrateCreativeShape (server
// read path, zod entry default, client draftToInput).
export function migrateAdditionalAds(ads: AdditionalAd[] | undefined): AdditionalAd[] {
  return (ads ?? []).map((a) => ({
    ...a,
    assetType: a.assetType === 'video' ? 'video' : 'image',
    // Per-ad Active/Off — entries created before the field existed default ACTIVE
    // (they were effectively active). Only an explicit 'PAUSED' is preserved.
    status: a.status === 'PAUSED' ? 'PAUSED' : 'ACTIVE',
  }));
}

export function migrateCreativeShape(creative: any, legacyStoryImage?: unknown): AdCreative {
  const c = (creative ?? {}) as Record<string, unknown>;
  // Drop the legacy keys from the output (current shape going forward).
  const {
    headline,
    primaryText,
    description,
    headlines: _h,
    primaryTexts: _p,
    descriptions: _d,
    useDynamicCreative: _dc,
    assetType,
    imageUrl: _iu,
    imagePath: _ip,
    videoUrl: _vu,
    videoPath: _vp,
    videoThumbnailUrl: _vtu,
    videoThumbnailPath: _vtp,
    feedAsset: _fa,
    storyAsset: _sa,
    ...rest
  } = c;
  void _h; void _p; void _d; void _dc; void _iu; void _ip; void _vu; void _vp; void _vtu; void _vtp; void _fa; void _sa;

  const headlines = toArray(c.headlines, headline);
  const primaryTexts = toArray(c.primaryTexts, primaryText);

  return {
    ...(rest as Omit<AdCreative, 'headlines' | 'primaryTexts' | 'descriptions' | 'useDynamicCreative' | 'assetType' | 'feedAsset' | 'storyAsset'>),
    // Phase 1 Dynamic Creative toggle: a stored boolean wins; legacy drafts that
    // predate the flag derive it from the array lengths (>1 of either = the old
    // always-on multi-text UI → keep publishing via dynamicCreative; 1+1 = a
    // standard single-text ad). Preserves the publish behaviour of every
    // pre-toggle draft.
    useDynamicCreative:
      typeof c.useDynamicCreative === 'boolean'
        ? c.useDynamicCreative
        : headlines.length > 1 || primaryTexts.length > 1,
    headlines,
    primaryTexts,
    descriptions: toArray(c.descriptions, description),
    assetType: assetType === 'video' ? 'video' : 'image',
    feedAsset: migrateFeedAsset(c),
    storyAsset: migrateStoryAsset(c, legacyStoryImage),
  };
}
